import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money, fmtDateTime, todayIn, DOC_STATUS } from "@/lib/helpers";
import { FUEL_STATE, ENVELOPES } from "@/components/FuelBadge";
import ChatImage from "@/components/ChatImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Search, History, SlidersHorizontal, Fuel, Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Calculs en centimes (comme le serveur) : 1 400,01 − 10 − 55,02 = 1 334,99 au centime près.
const cents = (v) => Math.round((parseFloat(v) || 0) * 100);
const euros = (c) => c / 100;
const frDay = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "");
const vehicleLabel = (v) => [v.brand, v.model].filter(Boolean).join(" ") + (v.plate ? ` (${v.plate})` : "");
const envLabel = (k) => ENVELOPES.find((e) => e.key === k)?.label || k;
// Ligne rouge sous un champ : pourquoi le bouton « Enregistrer » est grisé (lot 19).
const FieldError = ({ msg, testid }) => (msg ? <p className="text-xs text-red-600" data-testid={testid}>{msg}</p> : null);
const NEGATIVE_MSG = "Le montant ne peut pas être négatif.";

export default function FuelAccounts() {
  const { settings } = useSettings();
  const cur = settings?.currency || "EUR";
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [full, setFull] = useState({});
  const [query, setQuery] = useState("");
  const [recharge, setRecharge] = useState(null);  // { client_id } ou null
  const [conso, setConso] = useState(null);        // compte pour lequel on saisit un plein
  const [adjust, setAdjust] = useState(null);      // compte à corriger
  const [history, setHistory] = useState(null);    // client_id dont on affiche l'historique

  const load = useCallback(() => {
    api.get("/fuel-accounts").then((r) => setAccounts(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/settings/full").then((r) => setFull(r.data)).catch(() => setFull({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const shown = accounts.filter((a) => !q || (a.client_name || "").toLowerCase().includes(q));
  const pct = full.fuel_alert_pct ?? 15;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Prépayé</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Compte carburant</h1>
        </div>
        <Button className="gap-2 rounded-full shrink-0" onClick={() => setRecharge({ client_id: "" })} data-testid="new-fuel-recharge-button"><Plus className="h-4 w-4" /> Recharge</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Rechercher un client…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="fuel-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shown.length === 0 && <p className="text-sm text-muted-foreground">{q ? "Aucun compte ne correspond." : "Aucun compte carburant. Une première recharge ouvre le compte du client."}</p>}
        {shown.map((a) => (
          <div key={a.client_id} className="bg-card border border-border rounded-2xl shadow-card p-5 space-y-3 min-w-0" data-testid={`fuel-card-${a.client_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="font-display font-bold text-lg truncate min-w-0">{a.client_name || "—"}</div>
              <div className="text-right shrink-0">
                <div className={cn("font-display font-black text-2xl leading-none", a.total < 0 && "text-red-600")} data-testid={`fuel-total-${a.client_id}`}>{money(a.total, cur)}</div>
                <div className="text-[10px] uppercase tracking-overline text-muted-foreground mt-1">solde total</div>
              </div>
            </div>
            <div className="border border-border rounded-xl divide-y divide-border">
              {ENVELOPES.map(({ key, label }) => {
                const env = a[key];
                const st = FUEL_STATE[env.state] || FUEL_STATE.ok;
                return (
                  <div key={key} className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`fuel-env-${key}-${a.client_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{label}</div>
                      {env.last_recharge > 0 && <div className="text-xs text-muted-foreground">alerte sous {money(env.threshold, cur)} ({pct} % de {money(env.last_recharge, cur)})</div>}
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      {env.used && env.state !== "ok" && <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", st.cls)} data-testid={`fuel-state-${key}-${a.client_id}`}>{st.label}</span>}
                      <span className={cn("font-display font-bold tabular-nums", env.balance < 0 ? "text-red-600" : env.state === "low" ? "text-amber-600" : "")} data-testid={`fuel-balance-${key}-${a.client_id}`}>{money(env.balance, cur)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {a.unpaid_count > 0 && <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-full text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400" data-testid={`fuel-unpaid-${a.client_id}`}>{a.unpaid_count > 1 ? `${a.unpaid_count} factures non payées` : "Facture non payée"}</span>}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" className="gap-1" onClick={() => setConso(a)} data-testid={`fuel-conso-${a.client_id}`}><Fuel className="h-3.5 w-3.5" /> Plein</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setRecharge({ client_id: a.client_id })} data-testid={`fuel-recharge-${a.client_id}`}><Plus className="h-3.5 w-3.5" /> Recharger</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setAdjust(a)} data-testid={`fuel-adjust-${a.client_id}`}><SlidersHorizontal className="h-3.5 w-3.5" /> Corriger</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setHistory(a.client_id)} data-testid={`fuel-history-${a.client_id}`}><History className="h-3.5 w-3.5" /> Historique</Button>
            </div>
          </div>
        ))}
      </div>

      <RechargeDialog state={recharge} setState={setRecharge} clients={clients} cur={cur} onSaved={load} />
      <ConsumptionDialog account={conso} setAccount={setConso} vehicles={vehicles} defaultFee={full.fuel_service_fee ?? 10} tz={settings?.timezone} cur={cur} onSaved={load} />
      <AdjustDialog account={adjust} setAccount={setAdjust} cur={cur} onSaved={load} />
      <HistoryDialog clientId={history} setClientId={setHistory} cur={cur} onChanged={load} />
    </div>
  );
}

function RechargeDialog({ state, setState, clients, cur, onSaved }) {
  const [form, setForm] = useState({ client_id: "", fuel: "", service: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (state) setForm({ client_id: state.client_id || "", fuel: "", service: "" }); }, [state]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = cents(form.fuel) + cents(form.service);
  const sorted = [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
  // Montants jamais négatifs, total > 0 (le serveur refuse aussi, avant de créer la facture).
  const fuelErr = cents(form.fuel) < 0 ? NEGATIVE_MSG : "";
  const serviceErr = cents(form.service) < 0 ? NEGATIVE_MSG : "";
  const totalErr = !fuelErr && !serviceErr && total <= 0 ? "Indiquez un montant supérieur à 0 € (carburant et/ou frais de service)." : "";
  const invalid = !!(fuelErr || serviceErr || totalErr);

  const save = async () => {
    if (!form.client_id) { toast.error("Choisissez le client"); return; }
    if (invalid) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/fuel-accounts/${form.client_id}/recharge`, { fuel: euros(cents(form.fuel)), service: euros(cents(form.service)) });
      toast.success(`Compte rechargé — facture ${data.invoice_number} en brouillon dans Facturation`);
      setState(null); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) setState(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Recharger le compte carburant</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Client</Label>
            <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
              <SelectTrigger data-testid="fuel-recharge-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>{sorted.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Carburant</Label><Input type="number" min="0" step="0.01" value={form.fuel} onChange={(e) => set("fuel", e.target.value)} placeholder="0,00" data-testid="fuel-recharge-fuel" /><FieldError msg={fuelErr} testid="fuel-recharge-fuel-error" /></div>
            <div className="space-y-1.5"><Label>Frais de service</Label><Input type="number" min="0" step="0.01" value={form.service} onChange={(e) => set("service", e.target.value)} placeholder="0,00" data-testid="fuel-recharge-service" /><FieldError msg={serviceErr} testid="fuel-recharge-service-error" /></div>
          </div>
          <div className="flex items-center justify-between bg-muted/50 border border-border rounded-xl px-4 py-3">
            <span className="text-sm text-muted-foreground">Total facturé</span>
            <span className="font-display font-black text-xl" data-testid="fuel-recharge-total">{money(euros(total), cur)}</span>
          </div>
          <FieldError msg={totalErr} testid="fuel-recharge-total-error" />
          <p className="text-xs text-muted-foreground">Une facture brouillon est créée dans Facturation. Le carburant y figure en « débours » : il n'entre pas dans le chiffre d'affaires. Le solde est crédité tout de suite.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setState(null)}>Annuler</Button><Button onClick={save} disabled={saving || invalid} data-testid="fuel-recharge-save">Recharger</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsumptionDialog({ account, setAccount, vehicles, defaultFee, tz, cur, onSaved }) {
  const empty = () => ({ date: todayIn(tz), vehicle_id: "", fuel: "", service: String(defaultFee), photo: "", notes: "" });
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmNeg, setConfirmNeg] = useState(null); // textes des enveloppes qui passeraient sous zéro
  const fileRef = useRef(null);
  const mine = account ? vehicles.filter((v) => v.client_id === account.client_id) : [];
  useEffect(() => {
    if (!account) return;
    const own = vehicles.filter((v) => v.client_id === account.client_id);
    setForm({ ...empty(), vehicle_id: own.length === 1 ? own[0].id : "" });
    setConfirmNeg(null);
  }, [account]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const after = account ? {
    fuel: cents(account.fuel.balance) - cents(form.fuel),
    service: cents(account.service.balance) - cents(form.service),
  } : { fuel: 0, service: 0 };
  // Carburant obligatoire et > 0 ; frais à 0 acceptés (passage offert), jamais négatifs.
  const fuelErr = cents(form.fuel) <= 0 ? "Le montant du carburant doit être supérieur à 0 €." : "";
  const serviceErr = cents(form.service) < 0 ? "Les frais de service ne peuvent pas être négatifs." : "";
  const invalid = !!(fuelErr || serviceErr);

  const uploadTicket = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.path);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Échec de l'envoi de la photo"); }
    finally { setUploading(false); }
  };

  const check = () => {
    if (!form.date) { toast.error("Indiquez la date"); return; }
    if (!form.vehicle_id) { toast.error("Choisissez le véhicule"); return; }
    if (invalid) return;
    // Solde négatif accepté (« à régulariser »), mais seulement après confirmation.
    const neg = [];
    if (cents(form.fuel) && after.fuel < 0) neg.push(`Le solde carburant passera à ${money(euros(after.fuel), cur)}.`);
    if (cents(form.service) && after.service < 0) neg.push(`Le solde des frais de service passera à ${money(euros(after.service), cur)}.`);
    if (neg.length) { setConfirmNeg(neg); return; }
    save();
  };

  const save = async () => {
    setConfirmNeg(null); setSaving(true);
    try {
      await api.post(`/fuel-accounts/${account.client_id}/consumptions`, {
        date: form.date, vehicle_id: form.vehicle_id, fuel: euros(cents(form.fuel)), service: euros(cents(form.service)),
        photo: form.photo, notes: form.notes.trim() });
      toast.success("Plein enregistré"); setAccount(null); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={!!account} onOpenChange={(o) => { if (!o) setAccount(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Plein — {account?.client_name}</DialogTitle></DialogHeader>
          {account && <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="fuel-conso-date" /></div>
              <div className="space-y-1.5"><Label>Véhicule</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                  <SelectTrigger data-testid="fuel-conso-vehicle"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{mine.map((v) => <SelectItem key={v.id} value={v.id}>{vehicleLabel(v)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {mine.length === 0 && <p className="text-xs text-red-600" data-testid="fuel-conso-no-vehicle">Ce client n'a aucun véhicule : ajoutez-le d'abord sur sa fiche (onglet Clients).</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Carburant (ticket)</Label><Input type="number" min="0" step="0.01" value={form.fuel} onChange={(e) => set("fuel", e.target.value)} placeholder="0,00" data-testid="fuel-conso-fuel" /><FieldError msg={fuelErr} testid="fuel-conso-fuel-error" /></div>
              <div className="space-y-1.5"><Label>Frais de service</Label><Input type="number" min="0" step="0.01" value={form.service} onChange={(e) => set("service", e.target.value)} data-testid="fuel-conso-service" /><FieldError msg={serviceErr} testid="fuel-conso-service-error" /></div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Photo du ticket (facultative)</Label>
                <input type="file" accept="image/*" ref={fileRef} onChange={uploadTicket} className="hidden" data-testid="fuel-conso-photo-input" />
                {!form.photo && <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs text-accent flex items-center gap-1" data-testid="fuel-conso-photo-add">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Ajouter
                </button>}
              </div>
              {form.photo
                ? <div className="flex items-start gap-2" data-testid="fuel-conso-photo-preview"><ChatImage path={form.photo} className="h-24 w-24 rounded-lg object-cover border border-border" />
                    <button type="button" onClick={() => set("photo", "")} className="text-xs text-muted-foreground flex items-center gap-1" data-testid="fuel-conso-photo-remove"><X className="h-3.5 w-3.5" /> Retirer</button></div>
                : <div className="h-14 rounded-xl border border-dashed border-border grid place-items-center text-xs text-muted-foreground">Aucune photo</div>}
            </div>
            <div className="space-y-1.5"><Label>Note (facultative)</Label><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ex. station, kilométrage…" data-testid="fuel-conso-notes" /></div>
            <div className="bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm space-y-1" data-testid="fuel-conso-preview">
              <div className="flex justify-between"><span className="text-muted-foreground">Carburant après le plein</span><span className={cn("font-medium tabular-nums", after.fuel < 0 && "text-red-600")}>{money(euros(after.fuel), cur)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Frais de service après le plein</span><span className={cn("font-medium tabular-nums", after.service < 0 && "text-red-600")}>{money(euros(after.service), cur)}</span></div>
            </div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setAccount(null)}>Annuler</Button><Button onClick={check} disabled={saving || uploading || invalid} data-testid="fuel-conso-save">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmNeg} onOpenChange={(o) => { if (!o) setConfirmNeg(null); }}>
        <AlertDialogContent data-testid="fuel-negative-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Solde insuffisant</AlertDialogTitle>
            <AlertDialogDescription>{(confirmNeg || []).join(" ")} Le compte sera « à régulariser ». Enregistrer quand même ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fuel-negative-confirm-no">Retour</AlertDialogCancel>
            <AlertDialogAction onClick={save} data-testid="fuel-negative-confirm-yes">Enregistrer quand même</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AdjustDialog({ account, setAccount, cur, onSaved }) {
  const [envelope, setEnvelope] = useState("fuel");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { setEnvelope("fuel"); setAmount(""); setReason(""); }, [account]);
  const c = cents(amount);
  // Correction ≠ 0 (négative pour retirer du solde) ; le motif reste vérifié au clic.
  const amountErr = c === 0 ? "Indiquez un montant différent de 0 € (négatif pour retirer du solde)." : "";
  const save = async () => {
    if (amountErr) return;
    if (!reason.trim()) { toast.error("Le motif est obligatoire"); return; }
    try {
      await api.post(`/fuel-accounts/${account.client_id}/adjust`, { envelope, amount: euros(c), reason: reason.trim() });
      toast.success("Solde corrigé"); setAccount(null); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!account} onOpenChange={(o) => { if (!o) setAccount(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Corriger le solde</DialogTitle></DialogHeader>
        {account && <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{account.client_name}</p>
          <div className="space-y-1.5"><Label>Enveloppe</Label>
            <Select value={envelope} onValueChange={setEnvelope}>
              <SelectTrigger data-testid="fuel-adjust-envelope"><SelectValue /></SelectTrigger>
              <SelectContent>{ENVELOPES.map((e) => <SelectItem key={e.key} value={e.key}>{e.label} · solde {money(account[e.key].balance, cur)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Correction (€)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="-20 pour retirer, 15,50 pour ajouter" data-testid="fuel-adjust-amount" />
            <FieldError msg={amountErr} testid="fuel-adjust-amount-error" />
            {c !== 0 && <p className="text-xs text-muted-foreground" data-testid="fuel-adjust-preview">Nouveau solde {envLabel(envelope).toLowerCase()} : {money(euros(cents(account[envelope].balance) + c), cur)}</p>}
          </div>
          <div className="space-y-1.5"><Label>Motif (obligatoire)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. reprise du tableau, reliquat rendu au client…" data-testid="fuel-adjust-reason" />
          </div>
        </div>}
        <DialogFooter><Button variant="outline" onClick={() => setAccount(null)}>Annuler</Button><Button onClick={save} disabled={!!amountErr} data-testid="fuel-adjust-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ clientId, setClientId, cur, onChanged }) {
  const [data, setData] = useState(null);
  const [toCancel, setToCancel] = useState(null);
  const [reason, setReason] = useState("");
  const [bigPhoto, setBigPhoto] = useState(null);
  const reload = useCallback(() => {
    if (!clientId) return;
    api.get(`/fuel-accounts/${clientId}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [clientId]);
  useEffect(() => { setData(null); reload(); }, [reload]);

  const confirmCancel = async () => {
    const e = toCancel; setToCancel(null);
    try {
      const { data: res } = await api.post(`/fuel-accounts/entries/${e.id}/cancel`, { reason: reason.trim() });
      toast.success(res.invoice_deleted ? `Recharge annulée — brouillon ${res.invoice_deleted} supprimé` : "Mouvement annulé");
      reload(); onChanged();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const describe = (e) => {
    if (e.kind === "recharge") return {
      tag: "Recharge", color: "text-emerald-600",
      text: [cents(e.fuel) ? `Carburant +${money(e.fuel, cur)}` : "", cents(e.service) ? `Frais de service +${money(e.service, cur)}` : ""].filter(Boolean).join(" · "),
      sub: `Facture ${e.invoice_number}${e.invoice_status ? ` (${DOC_STATUS[e.invoice_status]?.label.toLowerCase()})` : ""}` };
    if (e.kind === "consumption") return {
      tag: "Plein", color: "text-sky-600",
      text: [cents(e.fuel) ? `Carburant −${money(e.fuel, cur)}` : "", cents(e.service) ? `Frais de service −${money(e.service, cur)}` : ""].filter(Boolean).join(" · "),
      sub: [e.vehicle, e.notes, e.photo ? "" : "sans ticket"].filter(Boolean).join(" · ") };
    return { tag: "Correction", color: "text-amber-600",
      text: `${envLabel(e.envelope)} ${e.amount > 0 ? "+" : "−"}${money(Math.abs(e.amount), cur)}`, sub: e.reason };
  };
  const cancellable = (e) => e.status !== "cancelled" && (e.kind === "consumption" || (e.kind === "recharge" && e.invoice_status === "draft"));

  return (
    <>
      <Dialog open={!!clientId} onOpenChange={(o) => { if (!o) setClientId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Historique{data ? ` — ${data.client_name}` : ""}</DialogTitle></DialogHeader>
          {data && <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Carburant {money(data.fuel.balance, cur)} · Frais de service {money(data.service.balance, cur)} · Total <span className="font-medium text-foreground">{money(data.total, cur)}</span></p>
            <div className="border border-border rounded-xl divide-y divide-border max-h-[60vh] overflow-y-auto" data-testid="fuel-history-list">
              {data.entries.length === 0 && <p className="p-4 text-sm text-muted-foreground">Aucun mouvement.</p>}
              {data.entries.map((e) => {
                const d = describe(e);
                const cancelled = e.status === "cancelled";
                return (
                  <div key={e.id} className={cn("px-4 py-2.5 text-sm flex gap-3", cancelled && "opacity-60")} data-testid={`fuel-entry-${e.id}`}>
                    {e.photo && <button type="button" onClick={() => setBigPhoto(e.photo)} className="shrink-0" title="Agrandir le ticket" data-testid={`fuel-entry-photo-${e.id}`}>
                      <ChatImage path={e.photo} className="h-14 w-14 rounded-lg object-cover border border-border" />
                    </button>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn("text-[10px] uppercase tracking-overline", d.color)}>{d.tag}{cancelled ? " · annulé" : ""}</span>
                        <span className="text-xs text-muted-foreground">{frDay(e.date)}</span>
                      </div>
                      <div className={cn(cancelled && "line-through")}>{d.text}</div>
                      {d.sub && <div className="text-xs text-muted-foreground break-words">{d.sub}</div>}
                      <div className="text-xs text-muted-foreground">Saisi par {e.by_name || "—"} le {fmtDateTime(e.created_at)}</div>
                      {cancelled && <div className="text-xs text-muted-foreground" data-testid={`fuel-entry-cancelled-${e.id}`}>Annulé par {e.cancelled_by_name || "—"} le {fmtDateTime(e.cancelled_at)}{e.cancel_reason ? ` : ${e.cancel_reason}` : ""}</div>}
                      {cancellable(e) && <button type="button" className="text-xs text-muted-foreground underline hover:text-accent mt-1" onClick={() => { setReason(""); setToCancel(e); }} data-testid={`fuel-entry-cancel-${e.id}`}>Annuler</button>}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">Un mouvement annulé reste visible. Une correction ne s'annule pas : saisissez une correction inverse.</p>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!bigPhoto} onOpenChange={(o) => { if (!o) setBigPhoto(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Ticket</DialogTitle></DialogHeader>
          {bigPhoto && <ChatImage path={bigPhoto} className="w-full max-h-[75vh] object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toCancel} onOpenChange={(o) => { if (!o) setToCancel(null); }}>
        <AlertDialogContent data-testid="fuel-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{toCancel?.kind === "recharge" ? "Annuler cette recharge ?" : "Annuler ce plein ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {toCancel?.kind === "recharge"
                ? `La facture brouillon ${toCancel?.invoice_number} sera supprimée : son numéro ne sera pas réutilisé, il manquera dans la suite de vos factures.`
                : "Le montant est rendu au solde. Le plein reste visible dans l'historique, marqué « annulé »."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (facultatif)" data-testid="fuel-cancel-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fuel-cancel-confirm-no">Retour</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} data-testid="fuel-cancel-confirm-yes">Annuler le mouvement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
